import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Environment } from '@thiememeulenhoff/types';
import { decodeJwt } from 'jose';
import Cookies from 'js-cookie';
import { Observable, of, ReplaySubject, Subject, switchMap, tap } from 'rxjs';
import { PLATFORM_TOKEN, TokenInterface } from '../interface';
import { AuthConfigToken } from '../ng-security.module';
import { ToegangProvider } from '../providers';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DEFAULT_ENDPOINTS: Record<Environment, string> = {
  [Environment.Development]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Test]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Acceptance]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Staging]:
    'https://platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Production]:
    'https://platforms.thiememeulenhoff.nl/api/v1/auth/token',
};

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  readonly #provider = inject(ToegangProvider);

  readonly #config = inject(AuthConfigToken);

  readonly #http = inject(HttpClient);

  private static loginSubject?: Subject<unknown>;

  #accessToken?: string;

  get isLoggedIn() {
    if (!this.#accessToken) {
      return false;
    }

    try {
      const decodedJwt = decodeJwt(this.#accessToken);

      if (decodedJwt.exp) {
        return decodedJwt.exp > Date.now() / 1000;
      }
    } catch (error) {
      // Catch
    }

    return false;
  }

  set accessToken(token: string | undefined) {
    this.#accessToken = token;
  }

  get accessToken() {
    return this.#accessToken;
  }

  get user() {
    return this.#provider.user;
  }

  get userIdentifierFromToken() {
    if (!this.#accessToken) {
      return undefined;
    }

    try {
      const decodedJwt = decodeJwt(this.#accessToken);

      return decodedJwt.sub;
    } catch (error) {
      return undefined;
    }
  }

  login() {
    if (PlatformService.loginSubject) {
      return PlatformService.loginSubject.asObservable();
    }

    PlatformService.loginSubject = new ReplaySubject<unknown>();
    const obs = PlatformService.loginSubject.asObservable();

    this.#provider
      .login()
      .pipe(
        switchMap(() => this.#fetchToken(this.#provider.token)),
        tap(() => {
          PlatformService.loginSubject = undefined;
        }),
      )
      .subscribe(PlatformService.loginSubject);

    return obs;
  }

  logout() {
    this.#provider.logout();
    Cookies.remove(PLATFORM_TOKEN);
  }

  refresh() {
    if (!this.#provider.token) {
      return of(null);
    }

    return this.#fetchToken(this.#provider.token);
  }

  initialize() {
    if (Cookies.get(PLATFORM_TOKEN)) {
      this.#accessToken = Cookies.get(PLATFORM_TOKEN);
    }

    this.#provider.initialize();
  }

  #fetchToken(providerToken: string): Observable<unknown> {
    if (this.isLoggedIn) {
      return of(null);
    }

    const payload = `grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${providerToken}`;

    return this.#http
      .post<TokenInterface>(
        DEFAULT_ENDPOINTS[this.#config.environment],
        payload,
      )
      .pipe(tap((token) => this.handleToken(token.access_token)));
  }

  handleToken(accessToken: string) {
    this.#accessToken = accessToken;
    Cookies.set(PLATFORM_TOKEN, accessToken, {
      sameSite: 'strict',
      secure: true,
    });
  }
}
